import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useDepartments } from "@/hooks/use-departments";
import { useEquipmentAccess, useEquipmentCategories, useEquipmentList } from "@/hooks/use-equipment";
import { useEquipmentBooking, useEquipmentBookingLines } from "@/hooks/use-equipment-bookings";
import { useCancelEquipmentBooking, useSaveEquipmentBooking } from "@/hooks/use-equipment-booking-mutations";
import { useProjects } from "@/hooks/use-projects";
import { useUsers } from "@/hooks/use-users";
import { hasMinRole } from "@/lib/constants";
import { todayIso } from "@/lib/equipment/insurance";
import { addDays } from "@/lib/equipment/availability";
import { copyLines, daysBetween, draftWarnings, isSearchableForBooking, lineLabel, unavailableReason, validateDraft, type DraftLine } from "@/lib/equipment/booking-draft";
import { BookingItemSearch } from "./BookingItemSearch";
import { BookingLinesTable } from "./BookingLinesTable";
import { BookingConflictNotice } from "./BookingConflictNotice";

export interface BookingDialogDefaults {
  projectId?: string | null;
  itemId?: string;
  dateFrom?: string;
}

interface BookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = ny booking. */
  bookingId: string | null;
  defaults?: BookingDialogDefaults;
}

const selectCls = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm";
const BOOKER_ROLES = new Set(["user", "admin", "superadmin"]);
let keySeq = 0;
const newKey = () => `ny-${++keySeq}`;

/**
 * Book utstyr (mockup F): prosjekt eller internt, standarddatoer, ansvarlig og linjer med egne datoer.
 * «Kopier booking» (VIB-2920) gjør en åpnet booking om til en ny med de samme linjene.
 */
export function BookingDialog({ open, onOpenChange, bookingId, defaults }: BookingDialogProps) {
  const { activeUser } = useImpersonation();
  const access = useEquipmentAccess();
  const { data: items = [] } = useEquipmentList(access.canManage, open && !access.isLoading);
  const { data: categories = [] } = useEquipmentCategories(open);
  const { data: departments = [] } = useDepartments();
  const { data: projects = [] } = useProjects();
  const { data: users = [] } = useUsers();
  const { data: booking } = useEquipmentBooking(open ? bookingId : null);
  const save = useSaveEquipmentBooking();
  const cancel = useCancelEquipmentBooking();

  const today = todayIso();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [responsibleId, setResponsibleId] = useState("");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [confirmCancel, setConfirmCancel] = useState(false);
  /** Satt mens en kopi lages: hva den er kopiert fra. Kopien lagres som ny booking. */
  const [copyOf, setCopyOf] = useState<string | null>(null);
  const editingId = copyOf ? null : bookingId;

  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const departmentNames = useMemo(() => new Map(departments.map((d) => [d.id, d.name])), [departments]);
  const departmentName = (id: string | null) => (id ? (departmentNames.get(id) ?? "–") : "Ukjent avdeling");

  // Fyll skjemaet når dialogen åpnes (ny) eller bookingen er lastet (endre).
  useEffect(() => {
    if (!open) {
      setCopyOf(null);
      return;
    }
    if (copyOf) return;
    setConfirmCancel(false);
    if (bookingId) {
      if (!booking) return;
      setProjectId(booking.project_id);
      setTitle(booking.title ?? "");
      setResponsibleId(booking.responsible_user_id);
      setLines(booking.lines.map((l) => ({ key: l.id, id: l.id, item_id: l.item_id, quantity: l.quantity, date_from: l.date_from, date_to: l.date_to, note: l.note ?? "" })));
      setDateFrom(booking.lines[0]?.date_from ?? today);
      setDateTo(booking.lines[0]?.date_to ?? today);
      return;
    }
    const from = defaults?.dateFrom ?? today;
    setProjectId(defaults?.projectId ?? null);
    setTitle("");
    setResponsibleId(activeUser?.id ?? "");
    setDateFrom(from);
    setDateTo(from);
    setLines(defaults?.itemId ? [{ key: newKey(), item_id: defaults.itemId, quantity: 1, date_from: from, date_to: from, note: "" }] : []);
  }, [open, bookingId, booking, defaults, activeUser?.id, today, copyOf]);

  const earliest = lines.reduce((min, l) => (l.date_from && l.date_from < min ? l.date_from : min), dateFrom);
  const { data: existing = [] } = useEquipmentBookingLines(earliest, open);

  const categoryBookable = useMemo(() => new Map(categories.map((c) => [c.id, c.bookable])), [categories]);
  // Utstyr på service eller savnet vises i søket, men kan ikke velges (VIB-2921).
  const pickable = useMemo(
    () =>
      items.filter(
        (i) =>
          isSearchableForBooking(i, categoryBookable) &&
          !lines.some((l) => l.item_id === i.id),
      ),
    [items, categoryBookable, lines],
  );
  const warnings = useMemo(
    () => draftWarnings({ drafts: lines, bookingId: editingId, existing, items: itemById }),
    [lines, editingId, existing, itemById],
  );
  const conflictLines = lines.filter((l) => (warnings.get(l.key)?.conflicts.length ?? 0) > 0);

  const projectOptions = useMemo(
    () => projects.filter((p) => p.project_status === "active" || p.project_status === "kommende" || p.id === projectId),
    [projects, projectId],
  );
  const project = projects.find((p) => p.id === projectId);
  const self = activeUser?.id;
  const canEdit =
    !editingId ||
    (!!booking && booking.status === "active" && (hasMinRole(activeUser?.role ?? "user", "admin") ||
      booking.created_by === self || booking.responsible_user_id === self ||
      (!!project && (project.production_lead_id === self || project.advisor_id === self))));

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const startCopy = () => {
    if (!booking) return;
    setCopyOf(booking.lines[0] ? lineLabel(booking.lines[0]) : "bookingen");
    setLines((ls) => copyLines(ls, 0, newKey));
    if (self) setResponsibleId(self);
    setConfirmCancel(false);
  };

  // I en kopi flytter ny fra-dato alle linjene like mange dager.
  const changeFrom = (value: string) => {
    if (copyOf && value) {
      const shift = daysBetween(dateFrom, value);
      setLines((ls) => copyLines(ls, shift, newKey));
      setDateTo(addDays(dateTo, shift));
    } else if (value > dateTo) {
      setDateTo(value);
    }
    setDateFrom(value);
  };

  const submit = async () => {
    const error = validateDraft({ projectId, title, lines });
    if (error) {
      toast.error(error);
      return;
    }
    try {
      const result = await save.mutateAsync({
        bookingId: editingId,
        previousProjectId: editingId ? (booking?.project_id ?? null) : null,
        input: {
          project_id: projectId,
          title: projectId ? null : title.trim(),
          responsible_user_id: responsibleId || self || "",
          items: lines.map((l) => ({ id: l.id, item_id: l.item_id, quantity: l.quantity, date_from: l.date_from, date_to: l.date_to, note: l.note.trim() || null })),
        },
      });
      if (result.conflicts.length > 0) {
        toast.warning(`Lagret med dobbelbooking på ${[...new Set(result.conflicts.map((c) => c.item_name))].join(", ")}`);
      } else {
        toast.success(editingId ? "Bookingen er lagret" : copyOf ? "Bookingen er kopiert" : "Utstyret er booket");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const doCancel = async () => {
    if (!bookingId) return;
    try {
      await cancel.mutateAsync({ bookingId, projectId: booking?.project_id ?? null });
      toast.success("Bookingen er avbestilt");
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const busy = save.isPending || cancel.isPending;
  const submitLabel = editingId
    ? conflictLines.length > 0 ? "Lagre likevel" : "Lagre"
    : conflictLines.length > 0 ? "Book likevel" : `Book ${lines.length} utstyr`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{copyOf ? "Kopier booking" : bookingId ? "Booking av utstyr" : "Book utstyr"}</DialogTitle>
          <DialogDescription>
            {copyOf
              ? `Kopi av ${copyOf}. Velg prosjekt og ny fra-dato, så flyttes alle linjene like mye.`
              : "Hver linje kan få egne datoer. Dobbelbooking gir advarsel, men kan lagres."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="booking-project">Prosjekt</Label>
            <select
              id="booking-project"
              className={selectCls}
              value={projectId ?? ""}
              disabled={!canEdit}
              onChange={(e) => setProjectId(e.target.value || null)}
            >
              <option value="">Internt (uten prosjekt)</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.project_code} {p.name}</option>
              ))}
            </select>
            {!projectId && (
              <Input value={title} disabled={!canEdit} onChange={(e) => setTitle(e.target.value)} placeholder="Tittel, f.eks. Internt opptak" aria-label="Tittel" />
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-responsible">Ansvarlig</Label>
            <select id="booking-responsible" className={selectCls} value={responsibleId} disabled={!canEdit} onChange={(e) => setResponsibleId(e.target.value)}>
              {users.filter((u) => BOOKER_ROLES.has(u.role) || u.id === responsibleId).map((u) => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-from">Fra</Label>
            <Input id="booking-from" type="date" value={dateFrom} disabled={!canEdit}
              onChange={(e) => changeFrom(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-to">Til</Label>
            <Input id="booking-to" type="date" value={dateTo} min={dateFrom} disabled={!canEdit} onChange={(e) => setDateTo(e.target.value)} />
            <p className="text-xs text-muted-foreground">Standarddatoer for utstyr du legger til.</p>
          </div>
        </div>
        {canEdit && (
          <div className="space-y-1.5">
            <Label>Utstyr</Label>
            <BookingItemSearch
              items={pickable}
              departmentName={departmentName}
              disabledReason={unavailableReason}
              onPick={(i) => setLines((ls) => [...ls, { key: newKey(), item_id: i.id, quantity: 1, date_from: dateFrom, date_to: dateTo, note: "" }])}
            />
          </div>
        )}
        <BookingLinesTable
          lines={lines}
          items={itemById}
          warnings={warnings}
          departmentName={departmentName}
          readOnly={!canEdit}
          onChange={updateLine}
          onRemove={(key) => setLines((ls) => ls.filter((l) => l.key !== key))}
        />
        <BookingConflictNotice lines={conflictLines} items={itemById} warnings={warnings} />
        {bookingId && booking && !canEdit && (
          <p className="text-sm text-muted-foreground">
            {booking.status === "cancelled"
              ? "Bookingen er avbestilt."
              : "Bare den som booket, den ansvarlige, PL eller rådgiver på prosjektet og administratorer kan endre bookingen."}
          </p>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            {bookingId && booking && !copyOf && (
              <Button variant="outline" onClick={startCopy} disabled={busy}>Kopier booking</Button>
            )}
            {editingId && canEdit && (
              confirmCancel ? (
                <Button variant="destructive" onClick={doCancel} disabled={busy}>Bekreft avbestilling</Button>
              ) : (
                <Button variant="outline" onClick={() => setConfirmCancel(true)} disabled={busy}>Avbestill booking</Button>
              )
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Avbryt</Button>
            {canEdit && (
              <Button onClick={submit} disabled={busy || lines.length === 0}>{submitLabel}</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
